import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';

/**
 * FavoriteButton — toggle a listing as bookmarked/favorited.
 * Variant `icon` renders as a small floating icon (for cards);
 * variant `default` renders as a full-width outline button (for detail pages).
 */
export const FavoriteButton = ({ listingId, variant = 'icon', className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || !listingId) { setLoaded(true); return; }
    api.get(`/favorites/check/${listingId}`)
      .then(r => setFav(!!r.data?.favorited))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [listingId, user?.id]);

  const toggle = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!user) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    setBusy(true);
    try {
      if (fav) {
        await api.delete(`/favorites/${listingId}`);
        setFav(false);
        toast.success('Removed from favorites');
      } else {
        await api.post(`/favorites/${listingId}`);
        setFav(true);
        toast.success('Saved to favorites');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (variant === 'icon') {
    return (
      <button
        type='button'
        onClick={toggle}
        disabled={busy || !loaded}
        aria-label={fav ? 'Remove from favorites' : 'Save to favorites'}
        data-testid='favorite-btn-icon'
        className={`h-8 w-8 rounded-full bg-background/90 border shadow-sm hover:bg-background flex items-center justify-center transition-colors ${className}`}
      >
        <Heart className={`h-4 w-4 ${fav ? 'fill-[hsl(var(--danger))] text-[hsl(var(--danger))]' : 'text-muted-foreground'}`} />
      </button>
    );
  }

  return (
    <Button
      type='button'
      variant={fav ? 'outline' : 'outline'}
      onClick={toggle}
      disabled={busy || !loaded}
      data-testid='favorite-btn-default'
      className={className}
    >
      <Heart className={`h-4 w-4 mr-1.5 ${fav ? 'fill-[hsl(var(--danger))] text-[hsl(var(--danger))]' : ''}`} />
      {fav ? 'Saved' : 'Save'}
    </Button>
  );
};

export default FavoriteButton;
